import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuInfo } from 'rc-menu/es/interface';
import { useTranslation } from 'react-i18next';
import { Flex, Select, Switch } from 'antd';
import type { MenuProps } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { PageLayout, SideBarProps, TopBarProps, type AboutInfo, VERSION as ENSO_VERSION } from '@ohpen/enso-ui';
import { getCdnAssets, useConfig, type CdnAssets } from '@ohpen/react-utils';
import { useAuthz } from '@ohpen/react-authz';
import { useAuth } from '@ohpen/react-auth';

import { AppMenuItem, AppLayoutProps } from './interfaces';
import { getSideBarItems } from './sideBarItems';
import { UserProfileModal } from './UserProfileModal';
import styles from './styles.module.css';
import { FeatureFlag, Permission, Role, getSignInMethod } from '../../contexts/AuthzProvider';
import { getUserGroups } from '../../contexts/AuthzProvider/utils';
import { useFeatureToggle } from '@pmp/hooks';
import { PmpConfigContext } from '@pmp/contexts';

// Cognito groups that map to a known PMP role. Anything else (other apps' groups,
// stray entries) is ignored when building the profile view.
const KNOWN_ROLES = new Set<Role>([
  'PMP_ReadOnly',
  'PMP_Update',
  'PMP_Reviewers',
  'PMP_Lenders',
  'PMP_LenderAdmin',
  'PMP_LenderViewer',
  'PMP_LenderSupport',
]);

export const AppLayout = ({ children }: AppLayoutProps): ReactElement => {
  const config = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const auth = useAuth();
  const { userPermissions, envFeatureFlags } = useAuthz();
  const enableDarkMode = useFeatureToggle(['enableDarkMode']);
  const pmpConfig = useContext(PmpConfigContext);
  const { CLIENT: client, STAGE: stage, DEVELOPMENT_OVERRIDES } = config;


  const [selectedClient, setSelectedClient] = useState<string>(client);
  const [cdnAssets, setCdnAssets] = useState<CdnAssets | null>(null);

  useEffect(() => {
    if (stage === 'dev') {
      setSelectedClient(localStorage.getItem('dev-theme') ?? client);
    }
  }, [client, stage]);

  useEffect(() => {
    getCdnAssets({ ...config, CLIENT: selectedClient }).then(assets => {
      if (assets) setCdnAssets(assets);
    });
  }, [config, selectedClient]);

  const onMenuItemClick = useCallback(
    (info: MenuInfo) => {
      navigate(`/${info.key}`);
    },
    [navigate],
  );

  const sideBarItems: AppMenuItem[] = useMemo(
    () => getSideBarItems(userPermissions as Set<Permission>, envFeatureFlags as Set<FeatureFlag>),
    [userPermissions, envFeatureFlags],
  );

  const selected = useMemo(() => {
    const key = location.pathname.split('/')[1];
    const item = sideBarItems?.find(item => item?.key === key);
    return item?.key;
  }, [location.pathname, sideBarItems]);

  const items: AppMenuItem[] = useMemo(
    () =>
      sideBarItems.map(item => (item && 'label' in item && typeof item.label === 'string' ? { ...item, label: t(item.label) } : item)) ??
      [],
    [sideBarItems, t],
  );

  const sideBar: SideBarProps = useMemo(
    () => ({
      items: items as any,
      selectedKeys: [String(selected)],
      onClick: onMenuItemClick,
      width: 220,
    }),
    [items, onMenuItemClick, selected],
  );

  const { userDisplayName: displayName } = auth;

  const [profileOpen, setProfileOpen] = useState(false);

  const userRoles = useMemo(() => {
    const groups = getUserGroups(auth.user?.attributes?.['custom:groups']);
    return groups.filter((r): r is Role => KNOWN_ROLES.has(r as Role));
  }, [auth.user?.attributes]);

  const signInMethod = useMemo(() => getSignInMethod(auth.user?.attributes), [auth.user?.attributes]);

  const userEmail = auth.user?.attributes?.email;

  // Dark mode is toggled here (rather than via enso's built-in showDarkModeToggle) so we
  // control the user-menu order: enso always renders its toggle after extraItems, but we
  // want "My profile" below dark mode. Same localStorage key / theme attribute as enso, so
  // persistence stays consistent.
  const [isDark, setIsDark] = useState(() => {
    if (localStorage.getItem('enso-dark-mode') === 'true') {
      document.documentElement.dataset.theme = 'dark';
      return true;
    }
    return document.documentElement.dataset.theme === 'dark';
  });

  const handleDarkModeToggle = useCallback((checked: boolean) => {
    document.documentElement.dataset.theme = checked ? 'dark' : '';
    localStorage.setItem('enso-dark-mode', String(checked));
    setIsDark(checked);
  }, []);

  // Order matters: dark mode first, then "My profile" (both before enso's About/Logout).
  const userMenuExtraItems: MenuProps['items'] = useMemo(() => {
    const entries: MenuProps['items'] = [];
    if (enableDarkMode) {
      entries.push({
        key: 'dark-mode',
        label: (
          <Flex justify="space-between" align="center" onClick={e => e.stopPropagation()}>
            <span>{t('global.labels.darkMode')}</span>
            <Switch size="small" checked={isDark} onChange={handleDarkModeToggle} />
          </Flex>
        ),
      });
    }
    entries.push({
      key: 'my-account',
      icon: <UserOutlined />,
      label: t('global.labels.myAccount'),
      onClick: () => setProfileOpen(true),
    });
    return entries;
  }, [enableDarkMode, isDark, handleDarkModeToggle, t]);

  const onClickLogout = useCallback(async () => {
    await auth.logout();
  }, [auth]);

  const onChangeClient = useCallback((value: string) => {
    setSelectedClient(value);
    localStorage.setItem('dev-theme', value);
  }, []);

  const customMenu = useMemo(() => {
    const clientsList = DEVELOPMENT_OVERRIDES?.CLIENTS_LIST;
    if (!clientsList || stage !== 'dev') return undefined;
    const options = clientsList.map((client: string) => ({ value: client, label: client }));

    return (
      <Select className={styles.clientSelect} value={selectedClient} onChange={onChangeClient} options={options} variant="borderless" />
    );
  }, [DEVELOPMENT_OVERRIDES?.CLIENTS_LIST, onChangeClient, selectedClient, stage]);

  const aboutInfo: AboutInfo = useMemo(
    () => ({
      items: [
        { label: t('global.labels.version'), value: config.VERSION || '-' },
        { label: t('global.labels.brsVersion'), value: pmpConfig?.brsVersion || '-' },
        { label: t('global.labels.stage'), value: config.STAGE || '-' },
        { label: t('global.labels.client'), value: config.CLIENT || '-' },
        { label: t('global.labels.ensoUi'), value: ENSO_VERSION },
      ],
    }),
    [config, pmpConfig, t],
  );

  const topBar: TopBarProps = useMemo(
    () => ({
      logo: { src: cdnAssets?.logoHorizontalPath ?? '', href: '/' },
      appName: t('global.title'),
      userMenu: {
        name: displayName,
        onClickLogout,
        aboutInfo,
        extraItems: userMenuExtraItems,
      },
      customMenu,
    }),
    [cdnAssets?.logoHorizontalPath, customMenu, onClickLogout, displayName, aboutInfo, userMenuExtraItems, t],
  );

  return (
    <PageLayout sideBar={sideBar} topBar={topBar}>
      <UserProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        name={displayName}
        email={userEmail}
        signInMethod={signInMethod}
        roles={userRoles}
      />
      {children}
    </PageLayout>
  );
};
